import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { WishlistFacade } from '../../facades/wishlist.facade';
import { takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../../analytics/analytics.service';
import { IBaseWishlist } from '../../models/wishlist.models';
import { ICheckboxChangeEvent } from '../../models/common.models';

@Component({
  selector: 'app-add-to-wishlist',
  templateUrl: './add-to-wishlist.component.html',
  styleUrls: ['./add-to-wishlist.component.scss'],
  standalone: false,
})
export class AddToWishlistComponent implements OnInit, OnDestroy {
  active: boolean = false;
  wishlistsInProgress: Array<{ id: string; sku: string }> = [];
  @Input() wishlists!: Array<IBaseWishlist>;
  @Input() sku!: string;
  @Input() productName!: string;
  @Input() productKeywords: string = '';
  @Input() textNextToIcon: boolean = false;
  @Input() addNewModalActive!: boolean;
  @Input() maxLength: number = 5;
  @Output() createNew: EventEmitter<void> = new EventEmitter<void>();
  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  private readonly wishlistFacade: WishlistFacade = inject(WishlistFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  ngOnInit(): void {
    this.getWishlistsFromStore();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getWishlistsFromStore(): void {
    this.wishlistFacade.selectListInProgress().pipe(
      takeUntil(this.unsubscribe$)).subscribe(wishlists => {
        this.wishlistsInProgress = wishlists;
      },
    );
  }

  toggle(wishlistId: string, event: ICheckboxChangeEvent): void {
    if (event.target.checked) {
      this.wishlistFacade.addNewItem(wishlistId, this.sku);
      this.trackProduct();
    } else {
      const shoppingListItemId: string = this.wishlists
        .find(list => list.id === wishlistId)?.items
        ?.find(item => '' + item.sku === '' + this.sku)?.shoppingListItemId ?? '';
      this.wishlistFacade.removeItem(wishlistId, shoppingListItemId, this.sku);
    }
  }

  addNew(): void {
    this.createNew.emit();
  }

  inProgress(wishlistId: string): boolean {
    return !!this.wishlistsInProgress.find(
      wishlist => wishlist.id === wishlistId && wishlist.sku === this.sku);
  }

  wishlistSelected(wishlistName: string): boolean {
    return !!this.wishlistsWithProduct().find(wishlist => wishlist.attributes.name === wishlistName);
  }

  wishlistsWithProduct(): Array<IBaseWishlist> {
    return this.wishlists.filter(
      wishlist => wishlist.items?.length && wishlist.items?.find(
        item => '' + item.sku === '' + this.sku));
  }

  private trackProduct(): void {
    const productData: { id: string; attributes: { name: string; metaKeywords: string } } = {
      id: this.sku,
      attributes: {
        name: this.productName,
        metaKeywords: this.productKeywords,
      },
    };
    this.analyticsService.trackProduct('product.favorite', productData);
  }
}
